import type { MapPoint } from "./directoryMapData";
import {
  haversineKm,
  type RoutePoint,
  type ValidPoint,
} from "./nearestNeighborRoute";

/** Paradas por ronda: valor por omisión del campo y tope (límite del orden O(n²)). */
export const DEFAULT_ROUND_SIZE = 9;
export const MAX_ROUND_SIZE = 25;
/** Filas que se piden por rectángulo (PLAN_RUTAS.md §D); se amplía el radio antes que este límite. */
export const NEARBY_FETCH_LIMIT = 500;
/** Radios (km) que se prueban en orden hasta juntar las paradas pedidas. */
export const RADIUS_STEPS_KM = [2, 5, 10, 25];

const KM_PER_DEGREE = 111;

/** Filtro PostgREST de un rectángulo (≈ km/111 grados) alrededor de `center`, sin PostGIS. */
export const boundingBoxFilter = (
  center: RoutePoint,
  km: number,
): Record<string, number> => {
  const dLat = km / KM_PER_DEGREE;
  const cos = Math.max(Math.cos((center.lat * Math.PI) / 180), 0.01);
  const dLng = km / (KM_PER_DEGREE * cos);
  return {
    "lat@gte": center.lat - dLat,
    "lat@lte": center.lat + dLat,
    "lng@gte": center.lng - dLng,
    "lng@lte": center.lng + dLng,
  };
};

export type NearbyCandidate = MapPoint & { company_id?: number | null };

export type NearbyExclusions = {
  /** ids de `directory` (p. ej. paradas ya incluidas en la ronda abierta) */
  ids?: ReadonlySet<number>;
  /** empresas con nota de visita (`contact_notes.channel = 'visita'`) */
  companyIds?: ReadonlySet<number>;
};

const isValid = (p: NearbyCandidate): p is NearbyCandidate & ValidPoint =>
  typeof p.lat === "number" &&
  typeof p.lng === "number" &&
  Number.isFinite(p.lat) &&
  Number.isFinite(p.lng) &&
  Math.abs(p.lat) <= 90 &&
  Math.abs(p.lng) <= 180;

/** Quita excluidas y sin coordenadas, ordena por distancia real a `center` y corta a `n`. */
export const pickNearby = (
  center: RoutePoint,
  rows: NearbyCandidate[],
  n: number,
  exclude: NearbyExclusions = {},
): (NearbyCandidate & ValidPoint)[] =>
  rows
    .filter(isValid)
    .filter(
      (r) =>
        !exclude.ids?.has(r.id) &&
        !(r.company_id != null && exclude.companyIds?.has(r.company_id)),
    )
    .map((r) => ({ r, km: haversineKm(center, r) }))
    .sort((a, b) => a.km - b.km || a.r.id - b.r.id)
    .slice(0, Math.max(0, n))
    .map(({ r }) => r);

/**
 * Busca hasta `n` paradas cerca de `center`: prueba rectángulos cada vez más grandes
 * (RADIUS_STEPS_KM) hasta juntar `n` o agotar los radios; devuelve lo que haya.
 * `fetchBox` trae las filas de un rectángulo (el llamador agrega el filtro activo).
 */
export const findNearbyStops = async (opts: {
  center: RoutePoint;
  n: number;
  exclude?: NearbyExclusions;
  fetchBox: (box: Record<string, number>) => Promise<NearbyCandidate[]>;
}): Promise<(NearbyCandidate & ValidPoint)[]> => {
  let found: (NearbyCandidate & ValidPoint)[] = [];
  for (const km of RADIUS_STEPS_KM) {
    const rows = await opts.fetchBox(boundingBoxFilter(opts.center, km));
    found = pickNearby(opts.center, rows, opts.n, opts.exclude);
    if (found.length >= opts.n) break;
  }
  return found;
};
